import React from "react";
import { Link } from "react-router-dom";
import PageShell from "../components/PageShell";
import PageHero from "../components/PageHero";
import Logo from "../components/Logo";

function Section({ id, label, title, kicker, children }) {
  return (
    <section
      id={id}
      data-testid={`brand-section-${id}`}
      className="py-24 md:py-32 border-t border-hairline"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="mb-12 md:mb-16 grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-7">
            <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
              ◇ {label}
            </span>
            <h2 className="mt-3 font-display text-4xl md:text-6xl font-medium tracking-tight text-primary-x">
              {title}
            </h2>
          </div>
          {kicker && (
            <p className="md:col-span-5 text-secondary-x leading-relaxed">{kicker}</p>
          )}
        </div>
        {children}
      </div>
    </section>
  );
}

function Swatch({ name, varName, hexDark, hexLight, role }) {
  return (
    <div className="rounded-2xl overflow-hidden border border-hairline bg-surface-2">
      <div
        className="aspect-[5/3] grid place-items-center"
        style={{ background: `var(${varName})` }}
      >
        <code className="text-[11px] font-mono-x" style={{ color: "var(--text-primary)", mixBlendMode: "difference" }}>
          {varName}
        </code>
      </div>
      <div className="p-5">
        <div className="text-primary-x text-sm font-medium">{name}</div>
        <div className="mt-1 text-[11px] tracking-[0.18em] uppercase text-muted-x font-mono-x">
          {role}
        </div>
        <div className="mt-3 text-[11px] font-mono-x text-secondary-x">
          dark <span className="text-primary-x">{hexDark}</span>
          <br />
          light <span className="text-primary-x">{hexLight}</span>
        </div>
      </div>
    </div>
  );
}

const TYPE_SAMPLES = [
  { label: "DISPLAY · CABINET GROTESK", className: "font-display text-5xl md:text-7xl font-bold tracking-[-0.04em] leading-[0.95]", sample: "Engineering the Impossible." },
  { label: "H2 · SECTION HEADING", className: "font-display text-4xl md:text-5xl font-medium tracking-tight", sample: "Three pillars. One studio." },
  { label: "H3 · SUB SECTION", className: "font-display text-2xl md:text-3xl font-medium tracking-tight", sample: "How we ship it." },
  { label: "BODY · MANROPE", className: "text-lg leading-relaxed text-secondary-x max-w-2xl", sample: "Ulties is a boutique software studio crafting web, mobile and desktop apps, production-grade AI agents and cyber-security systems for ambitious teams." },
  { label: "EYEBROW · JETBRAINS MONO", className: "text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x", sample: "◇ Services · Engineering" },
];

const SPACING = [
  ["1", "4 px", 4],
  ["2", "8 px", 8],
  ["3", "12 px", 12],
  ["4", "16 px", 16],
  ["6", "24 px", 24],
  ["8", "32 px", 32],
  ["12", "48 px", 48],
  ["16", "64 px", 64],
  ["24", "96 px", 96],
];

const RADII = [
  ["lg", "8 px", "rounded-lg"],
  ["xl", "12 px", "rounded-xl"],
  ["2xl", "16 px", "rounded-2xl"],
  ["3xl", "24 px", "rounded-3xl"],
  ["full", "9999 px", "rounded-full"],
];

export default function BrandPage() {
  return (
    <PageShell>
      <PageHero
        eyebrow="◇ Brand"
        title="The Ulties brand book."
        accent="book."
        kicker="The visual companion to /design.md — everything that makes a screen look like Ulties. Voice, mark, colour, type, motion, components. Read this before shipping any Ulties-facing surface."
      />

      {/* Quick jump */}
      <div className="sticky top-16 md:top-20 z-30 bg-canvas/80 backdrop-blur-2xl border-b border-hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex items-center gap-2 overflow-x-auto text-xs">
          <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mr-3 shrink-0">
            Jump to
          </span>
          {["voice", "logo", "color", "type", "spacing", "radius", "motion", "buttons", "downloads"].map((k) => (
            <a
              key={k}
              href={`#${k}`}
              data-testid={`brand-jump-${k}`}
              className="shrink-0 px-3 py-1.5 rounded-full border border-hairline-strong text-secondary-x hover:text-primary-x hover:bg-glass-hover transition-colors capitalize"
            >
              {k}
            </a>
          ))}
        </div>
      </div>

      {/* VOICE */}
      <Section id="voice" label="Voice" title="How we sound." kicker="Calm, direct, technically literate. We make claims because we've shipped the receipts. We never sell.">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-hairline bg-glass p-8">
            <div className="text-[11px] tracking-[0.25em] uppercase text-emerald-400 font-mono-x mb-4">DO</div>
            <ul className="space-y-3 text-primary-x">
              <li>— Short sentences. One claim at a time.</li>
              <li>— First-person plural: <em className="not-italic text-secondary-x">"we ship", "we built"</em>.</li>
              <li>— Sentence case everywhere — including headings.</li>
              <li>— Em-dash for emphasis. Oxford commas always.</li>
              <li>— Italicise one word for cadence: <em>recently.</em></li>
            </ul>
          </div>
          <div className="rounded-2xl border border-hairline bg-glass p-8">
            <div className="text-[11px] tracking-[0.25em] uppercase text-red-400 font-mono-x mb-4">DON'T</div>
            <ul className="space-y-3 text-primary-x">
              <li>— Title Case In Headings.</li>
              <li>— Buzzwords: <em className="not-italic text-secondary-x">"synergy", "robust", "world-class"</em>.</li>
              <li>— Exclamation marks. Ellipses for drama.</li>
              <li>— "Our team", "the team" — write "we".</li>
              <li>— Walls of text. Break for breath.</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* LOGO */}
      <Section id="logo" label="Logo" title="The mark." kicker="Three variants, one source of truth. Fill = currentColor, so the mark inherits the colour of whatever sits around it.">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="rounded-2xl border border-hairline bg-surface-2 p-8 grid place-items-center aspect-[4/3]">
            <Logo variant="mark" className="h-24 w-auto text-primary-x" />
          </div>
          <div className="rounded-2xl border border-hairline bg-surface-2 p-8 grid place-items-center aspect-[4/3]">
            <Logo variant="wordmark" className="h-20 w-auto text-primary-x" />
          </div>
          <div className="rounded-2xl border border-hairline bg-surface-2 p-8 grid place-items-center aspect-[4/3]">
            <Logo variant="full" className="h-28 w-auto text-primary-x" />
          </div>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/logo"
            data-testid="brand-logo-cta"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity"
            style={{ background: "var(--text-primary)", color: "var(--canvas)" }}
          >
            Open the full logo guide →
          </Link>
          <a
            href="/design.md"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-hairline-strong px-5 py-2.5 text-sm text-primary-x hover-glass transition-colors"
          >
            View design.md ↗
          </a>
        </div>
      </Section>

      {/* COLOR */}
      <Section id="color" label="Colour" title="Dark-first palette." kicker="Every visual decision must work in dark mode first. The light theme is a faithful inversion — same hierarchy, same contrast, opposite ends.">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Swatch name="Canvas" varName="--canvas" hexDark="#050505" hexLight="#fafafa" role="Page background" />
          <Swatch name="Surface" varName="--surface" hexDark="#070707" hexLight="#f4f4f5" role="Raised sections" />
          <Swatch name="Surface 2" varName="--surface-2" hexDark="#0a0a0a" hexLight="#ffffff" role="Cards, images" />
          <Swatch name="Glass" varName="--glass" hexDark="rgba(255,255,255,.02)" hexLight="rgba(0,0,0,.03)" role="Glass surfaces" />
          <Swatch name="Text primary" varName="--text-primary" hexDark="#fafafa" hexLight="#0a0a0a" role="Headlines, UI" />
          <Swatch name="Text secondary" varName="--text-secondary" hexDark="#a1a1aa" hexLight="#3f3f46" role="Body copy" />
          <Swatch name="Text muted" varName="--text-muted" hexDark="#52525b" hexLight="#71717a" role="Labels, eyebrows" />
          <Swatch name="Hairline" varName="--hairline" hexDark="rgba(255,255,255,.06)" hexLight="rgba(0,0,0,.07)" role="Borders, rules" />
        </div>

        <div className="mt-10 grid md:grid-cols-4 gap-6">
          <div className="rounded-2xl border border-hairline overflow-hidden">
            <div className="aspect-[5/3] grid place-items-center" style={{ background: "#022c22" }}>
              <span className="text-emerald-400 font-mono-x text-xs">SIGNAL</span>
            </div>
            <div className="p-4">
              <div className="text-primary-x text-sm">Signal green</div>
              <div className="text-[11px] font-mono-x text-muted-x mt-1">#34d399 · status only</div>
            </div>
          </div>
          <div className="rounded-2xl border border-hairline overflow-hidden">
            <div className="aspect-[5/3] grid place-items-center" style={{ background: "#0a0a0a" }}>
              <span className="font-mono-x text-xs" style={{ color: "#22d3ee" }}>GLITCH</span>
            </div>
            <div className="p-4">
              <div className="text-primary-x text-sm">Glitch cyan</div>
              <div className="text-[11px] font-mono-x text-muted-x mt-1">#22d3ee · motion only</div>
            </div>
          </div>
          <div className="rounded-2xl border border-hairline overflow-hidden">
            <div className="aspect-[5/3] grid place-items-center" style={{ background: "#0a0a0a" }}>
              <span className="font-mono-x text-xs" style={{ color: "#ec4899" }}>GLITCH</span>
            </div>
            <div className="p-4">
              <div className="text-primary-x text-sm">Glitch magenta</div>
              <div className="text-[11px] font-mono-x text-muted-x mt-1">#ec4899 · motion only</div>
            </div>
          </div>
          <div className="rounded-2xl border border-hairline overflow-hidden">
            <div className="aspect-[5/3] grid place-items-center" style={{ background: "#0a0a0a" }}>
              <span className="font-mono-x text-xs" style={{ color: "#a78bfa" }}>PURPLE</span>
            </div>
            <div className="p-4">
              <div className="text-primary-x text-sm">Brand purple</div>
              <div className="text-[11px] font-mono-x text-muted-x mt-1">#a78bfa · 3D rim only</div>
            </div>
          </div>
        </div>
      </Section>

      {/* TYPOGRAPHY */}
      <Section id="type" label="Typography" title="Three faces." kicker="Cabinet Grotesk for display. Manrope for body. JetBrains Mono for labels. No fourth typeface — ever.">
        <div className="rounded-2xl border border-hairline bg-surface-2 divide-y divide-hairline">
          {TYPE_SAMPLES.map((s) => (
            <div key={s.label} className="px-6 md:px-10 py-8 grid md:grid-cols-[240px_1fr] gap-6 items-baseline">
              <div className="text-[11px] tracking-[0.22em] uppercase text-muted-x font-mono-x">
                {s.label}
              </div>
              <div className={`${s.className} ${s.label.includes("BODY") || s.label.includes("EYEBROW") ? "" : "text-primary-x text-balance"}`}>
                {s.sample}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* SPACING */}
      <Section id="spacing" label="Spacing" title="Generous rhythm." kicker="Ulties uses ~2× the spacing of most agency sites. Sections breathe.">
        <div className="rounded-2xl border border-hairline bg-surface-2 p-8 md:p-10">
          <div className="space-y-3">
            {SPACING.map(([t, px, w]) => (
              <div key={t} className="flex items-center gap-6">
                <div className="w-16 text-[11px] tracking-[0.18em] uppercase text-muted-x font-mono-x">
                  {t}
                </div>
                <div className="w-24 text-secondary-x text-sm">{px}</div>
                <div
                  className="h-3 rounded-full"
                  style={{ width: w, background: "var(--text-primary)" }}
                />
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* RADIUS */}
      <Section id="radius" label="Radius" title="Pills and panels." kicker="Buttons are always pill-shaped. Cards always 2xl (16 px). Everything else picks from the scale.">
        <div className="grid md:grid-cols-5 gap-6">
          {RADII.map(([t, px, cls]) => (
            <div key={t} className="text-center">
              <div className={`mx-auto h-24 w-24 ${cls} bg-glass border border-hairline-strong`} />
              <div className="mt-4 text-primary-x text-sm font-mono-x">{t}</div>
              <div className="text-[11px] text-muted-x font-mono-x">{px}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* MOTION */}
      <Section id="motion" label="Motion" title="Confident, never twitchy." kicker="Easing is one custom expo-out curve. Entrances reveal, hovers respond, loops are rare and on-brand.">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-hairline bg-glass p-8">
            <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-6">Tokens</div>
            <pre className="text-sm text-primary-x font-mono-x leading-relaxed overflow-auto">{`easing  cubic-bezier(0.22, 1, 0.36, 1)
entrance  y:30→0, opacity:0→1
duration  0.6–0.9s entrance
          ≤0.35s hover
stagger   0.06–0.08s siblings
scroll    lenis smooth
library   framer-motion`}</pre>
          </div>
          <div className="rounded-2xl border border-hairline bg-glass p-8">
            <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-6">Rules</div>
            <ul className="space-y-3 text-primary-x leading-relaxed">
              <li>— Never animate <code className="font-mono-x text-muted-x">transition: all</code>.</li>
              <li>— Target opacity, transform, background-color.</li>
              <li>— Marquees and shimmer = CSS keyframes only.</li>
              <li>— Three.js scenes re-mount via <code className="font-mono-x text-muted-x">key={`{theme}`}</code> on flip.</li>
              <li>— Respect <code className="font-mono-x text-muted-x">prefers-reduced-motion</code> (TODO).</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* BUTTONS */}
      <Section id="buttons" label="Buttons" title="Pills. Always pills." kicker="One primary, one glass secondary, one tertiary link. That's the whole library.">
        <div className="rounded-2xl border border-hairline bg-surface-2 p-10 grid md:grid-cols-3 gap-8 items-center">
          <div className="text-center">
            <button
              type="button"
              data-testid="brand-btn-primary"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity"
              style={{ background: "var(--text-primary)", color: "var(--canvas)" }}
            >
              Start a project →
            </button>
            <div className="mt-4 text-[11px] tracking-[0.22em] uppercase text-muted-x font-mono-x">PRIMARY</div>
          </div>
          <div className="text-center">
            <button
              type="button"
              data-testid="brand-btn-glass"
              className="inline-flex items-center gap-2 rounded-full border border-hairline-strong bg-glass backdrop-blur-xl px-6 py-3 text-sm text-primary-x hover-glass transition-colors"
            >
              View work →
            </button>
            <div className="mt-4 text-[11px] tracking-[0.22em] uppercase text-muted-x font-mono-x">SECONDARY · GLASS</div>
          </div>
          <div className="text-center">
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              data-testid="brand-btn-link"
              className="text-secondary-x hover:text-primary-x transition-colors text-sm"
            >
              All services →
            </a>
            <div className="mt-4 text-[11px] tracking-[0.22em] uppercase text-muted-x font-mono-x">TERTIARY · LINK</div>
          </div>
        </div>
      </Section>

      {/* DOWNLOADS */}
      <Section id="downloads" label="Downloads" title="Pick up the pack." kicker="Everything you need to build something Ulties-shaped, in one place.">
        <div className="grid md:grid-cols-3 gap-6">
          <a
            href="/design.md"
            target="_blank"
            rel="noreferrer"
            data-testid="brand-download-md"
            className="group rounded-2xl border border-hairline bg-glass p-8 hover:border-hairline-strong transition-colors"
          >
            <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-6">LLM / Agent</div>
            <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-primary-x">
              design.md
            </h3>
            <p className="mt-4 text-secondary-x leading-relaxed">
              Machine-readable design system. Drop into any AI agent context for on-brand output.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-primary-x text-sm">
              Open <span className="transition-transform group-hover:translate-x-0.5">→</span>
            </span>
          </a>
          <a
            href="/logo.svg"
            download
            data-testid="brand-download-svg"
            className="group rounded-2xl border border-hairline bg-glass p-8 hover:border-hairline-strong transition-colors"
          >
            <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-6">Mark</div>
            <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-primary-x">
              logo.svg
            </h3>
            <p className="mt-4 text-secondary-x leading-relaxed">
              The native vector mark. currentColor fill — theme-aware out of the box.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-primary-x text-sm">
              Download <span className="transition-transform group-hover:translate-x-0.5">↓</span>
            </span>
          </a>
          <Link
            to="/logo"
            data-testid="brand-link-logo"
            className="group rounded-2xl border border-hairline bg-glass p-8 hover:border-hairline-strong transition-colors block"
          >
            <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-6">Logo / motion</div>
            <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-primary-x">
              Brand showcase
            </h3>
            <p className="mt-4 text-secondary-x leading-relaxed">
              Live demos of every approved logo variant, colour treatment and motion.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-primary-x text-sm">
              Open <span className="transition-transform group-hover:translate-x-0.5">→</span>
            </span>
          </Link>
        </div>
      </Section>
    </PageShell>
  );
}
